import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    MenuItem,
    alpha,
    useTheme,
    IconButton,
    Avatar,
    Snackbar,
    Alert,
    Divider,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import { withdrawFunds } from '../../services/backendAPI';
import { fetchProfileDataAction } from '../../store/actions/profile';
import AddBankModal from '../PayIn/AddBankModal';

const WithdrawModal = ({ open, onClose }) => {
    const theme = useTheme();
    const userData = useSelector(state => state.profile.userData);
    const bankAccounts = userData?.bank_accounts;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [amount, setAmount] = useState('100');
    const [selectedBank, setSelectedBank] = useState(bankAccounts?.[0]?.bank_account_number || '');
    const [amountError, setAmountError] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error'
    });
    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (open) {
            setAmount('100');
            if (bankAccounts?.length > 0) {
                setSelectedBank(bankAccounts[0].bank_account_number);
            }
        }
    }, [open, bankAccounts]);

    const handleAmountChange = (event) => {
        const value = event.target.value;
        const cleanValue = value.replace(/[^\d.]/g, '');
        const parts = cleanValue.split('.');
        const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts[1].slice(0, 2) : '');

        if (parseFloat(formattedValue) < 100) {
            setAmountError('Minimum withdrawal amount is ₹100');
            // } else if (parseFloat(formattedValue) > 1000000) {
            //     setAmountError('Maximum withdrawal amount is ₹10,00,000');
        } else {
            setAmountError('');
        }

        setAmount(formattedValue);
    };

    const handleWithdraw = async () => {
        setIsWithdrawing(true);
        try {
            const { data } = await withdrawFunds({
                amount: String(Number(amount) * 100),
                client_id: localStorage.getItem("login_id"),
                bank_account_number: selectedBank,
                bank_name: bankAccounts.find(bank => bank.bank_account_number === selectedBank).bank_name,
                ifsc: bankAccounts.find(bank => bank.bank_account_number === selectedBank).ifsc
            });
            setSnackbar({
                open: true,
                message: 'Withdrawal request submitted successfully',
                severity: 'success'
            });
            setTimeout(() => {
                onClose();
                setAmount('');
                setSelectedBank('');
                setIsWithdrawing(false);
            }, 2000);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error || 'Failed to process withdrawal request',
                severity: 'error'
            });
            setIsWithdrawing(false);
        }
    };

    const handleAddBankClose = () => {
        setShowAddBankModal(false);
    };

    const handleAddBankClick = () => {
        setShowAddBankModal(true);
    };

    const isValidForm = amount && !amountError && selectedBank;

    return (
        <>
            {!showAddBankModal ? (
                <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth="xs"
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={{
                        sx: {
                            borderRadius: isMobile ? 0 : 3,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                            maxWidth: isMobile ? '100%' : '400px !important',
                            height: isMobile ? '100%' : 'auto',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                >
                    <Box sx={{
                        background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        p: isMobile ? 2 : 2.5,
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            opacity: 0.1,
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                            backgroundSize: '24px 24px',
                        }} />
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            pt: isMobile ? 'env(safe-area-inset-top)' : 0
                        }}>
                            <Box>
                                <Typography variant="h6" sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    mb: 0.5,
                                    fontSize: isMobile ? '1.25rem' : '1.125rem'
                                }}>
                                    Withdraw Funds
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Transfer money to your bank account
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <DialogContent sx={{
                        p: 2.5,
                        flex: isMobile ? 1 : 'initial',
                        overflow: 'auto'
                    }}>
                        <Box sx={{
                            mb: 3,
                            p: 2,
                            bgcolor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.light, 0.05),
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}>
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Amount to Withdraw
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Min: ₹100
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                placeholder="0.00"
                                value={amount}
                                onChange={handleAmountChange}
                                error={Boolean(amountError)}
                                helperText={amountError}
                                InputProps={{
                                    startAdornment: (
                                        <Typography sx={{
                                            fontSize: '1.25rem',
                                            color: theme.palette.text.primary,
                                            mr: 1,
                                            fontWeight: 500
                                        }}>₹</Typography>
                                    )
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: theme.palette.background.paper,
                                    },
                                    '& input': {
                                        fontSize: '1.5rem',
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        py: 1.5,
                                        color: theme.palette.primary.main
                                    }
                                }}
                            />
                        </Box>

                        <Box>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1.5
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Select Bank Account
                                </Typography>
                                {
                                    bankAccounts?.length > 0 && ( <Button
                                        startIcon={<AddIcon />}
                                        onClick={handleAddBankClick}
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            color: theme.palette.primary.main,
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.08)
                                            }
                                        }}
                                    >
                                        Add Bank
                                    </Button>
                                )}

                            </Box>
                            
                            {bankAccounts?.length === 0 ? (
                                <Box sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    border: `1px dashed ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.action.hover, 0.05)
                                }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        No bank accounts added yet
                                    </Typography>
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => setShowAddBankModal(true)}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: 1.5
                                        }}
                                    >
                                        Add Your First Bank Account
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                }}>
                                    {bankAccounts?.map((bank, index) => (
                                        <Box key={bank.bank_account_number}>
                                            <Box
                                                onClick={() => setSelectedBank(bank.bank_account_number)}
                                                sx={{
                                                    p: 1.5,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    bgcolor: selectedBank === bank.bank_account_number
                                                        ? alpha(theme.palette.primary.main, 0.08)
                                                        : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: selectedBank === bank.bank_account_number
                                                            ? alpha(theme.palette.primary.main, 0.12)
                                                            : alpha(theme.palette.action.hover, 0.05)
                                                    }
                                                }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        bgcolor: selectedBank === bank.bank_account_number
                                                            ? alpha(theme.palette.primary.main, 0.2)
                                                            : alpha(theme.palette.primary.main, 0.1)
                                                    }}
                                                >
                                                    <AccountBalanceIcon
                                                        sx={{
                                                            fontSize: 20,
                                                            color: selectedBank === bank.bank_account_number
                                                                ? theme.palette.primary.main
                                                                : alpha(theme.palette.primary.main, 0.7)
                                                        }}
                                                    />
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {bank.bank_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        A/C: {bank.bank_account_number}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    border: `2px solid ${selectedBank === bank.bank_account_number
                                                        ? theme.palette.primary.main
                                                        : theme.palette.divider}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    {selectedBank === bank.bank_account_number && (
                                                        <Box sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: theme.palette.primary.main
                                                        }} />
                                                    )}
                                                </Box>
                                            </Box>
                                            {index < bankAccounts.length - 1 && <Divider />}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{
                        p: 2.5,
                        bgcolor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.5)
                            : alpha(theme.palette.grey[50], 0.5),
                        borderTop: `1px solid ${theme.palette.divider}`,
                        pb: isMobile ? 'calc(env(safe-area-inset-bottom) + 20px)' : 2.5
                    }}>
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: isMobile ? 1.5 : 1,
                            justifyContent: 'flex-end'
                        }}>
                            <Button
                                onClick={onClose}
                                size={isMobile ? "large" : "small"}
                                fullWidth={isMobile}
                                variant={isMobile ? "outlined" : "text"}
                                sx={{
                                    color: theme.palette.text.secondary,
                                    order: isMobile ? 2 : 1
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleWithdraw}
                                disabled={!isValidForm || isWithdrawing}
                                endIcon={<ArrowForwardIcon />}
                                size={isMobile ? "large" : "small"}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: 1.5,
                                    px: 2,
                                    bgcolor: theme.palette.success.main,
                                    '&:hover': {
                                        bgcolor: theme.palette.success.dark
                                    },
                                    order: isMobile ? 1 : 2
                                }}
                            >
                                {isWithdrawing ? 'Processing...' : 'Withdraw'}
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>
            ) : (
                <AddBankModal
                    open={true}
                    onClose={() => {
                        setShowAddBankModal(false);
                        dispatch(fetchProfileDataAction()); // Refresh bank list
                    }}
                />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: isMobile ? 'center' : 'right'
                }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '400px'
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default WithdrawModal;